import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Filter,
  LayoutGrid,
  LayoutList,
  Loader2,
  Eye,
  Lock,
  Minus,
  Search,
  SlidersHorizontal,
  Table2,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { DataTable } from '~/components/common/data-table/data-table';
import type { DataTableColumn } from '~/components/common/data-table/data-table.types';
import { FullPagination } from '~/components/common/full-pagination/full-pagination';
import { ResizableSplit } from '~/components/common/resizable-split/resizable-split';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { SplitLabelCell } from '~/components/common/split-label-cell';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import {
  EnrichmentFailedBadge,
  EnrichmentPhaseBadge,
  RecuratedSinceChip,
} from '~/features/catalog-operations/components/chips';
import { ProductCategory } from '~/features/catalog-operations/components/product-category';
import { ProductFilterDrawer } from '~/features/catalog-operations/components/product-filter-drawer';
import {
  AppliedFilterChips,
  ProductFilterRail,
} from '~/features/catalog-operations/components/product-filter-rail';
import { ProductQuickViewDialog } from '~/features/catalog-operations/components/product-quick-view-dialog';
import { scoreBadge, scoreTone } from '~/features/catalog-operations/components/product-score';
import {
  ProductsGridView,
  type GridDensity,
} from '~/features/catalog-operations/components/products-grid-view';
import { PAGE_SIZE_OPTIONS } from '~/features/catalog-operations/constants/products-list';
import type {
  CurationFilterDefinition,
  Product,
  ProductFilterValues,
  ProductTab,
} from '~/features/catalog-operations/types/catalog-operations.types';
import {
  usesNeedsAttentionDetailRoute,
  type ProductLinkContext,
} from '~/features/catalog-operations/utils/product-detail-link';
import { countAppliedFilters } from '~/features/catalog-operations/utils/product-filters';
import { useTableColumnOrder } from '~/hooks/use-table-column-order';
import {
  useTableColumnVisibility,
  type ColumnVisibilityDefault,
} from '~/hooks/use-table-column-visibility';
import { cn } from '~/utils/cn';

/** Render up to `max` tag badges, collapsing the remainder behind a "+N more"
 *  toggle that expands the rest in place. Expanding grows only this cell's row;
 *  trends and tags each own their toggle, so opening one leaves the other shut. */
function TagBadges({
  items,
  max = 1,
  className,
  testId,
}: {
  items: string[];
  max?: number;
  className?: string;
  /** Automation hook: prefixes each badge (`${testId}-badge`) and the overflow toggle. */
  testId?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, max);
  const overflow = items.length - max;
  return (
    <div data-testid={testId} className="flex flex-wrap items-center gap-1">
      {visible.map((t) => (
        <Badge
          key={t}
          data-testid={testId ? `${testId}-badge` : undefined}
          variant="outline"
          className={cn(
            'rounded-md px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
            className,
          )}
        >
          {t}
        </Badge>
      ))}
      {overflow > 0 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={testId ? `${testId}-more-toggle` : undefined}
          aria-expanded={expanded}
          aria-label={expanded ? 'Show fewer' : `Show ${String(overflow)} more`}
          // The row itself navigates to the product detail page — without this the
          // first click would open the product instead of revealing the tags.
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          // Sized to sit in the chip run: the toggle is a control, not the point
          // of the cell, so it shouldn't outweigh the values it hides.
          className="text-muted-foreground hover:text-foreground h-auto cursor-pointer rounded-md px-1.5 py-0.5 text-[11px] font-semibold"
        >
          {expanded ? 'Less' : `+${String(overflow)}`}
        </Button>
      )}
    </div>
  );
}

/** Candidate separators for product IDs (SKU / external id) — the display
 *  cell splits on whichever occurs last, since the separator varies by source. */
const ID_DELIMITERS = ['-', '_', '/'] as const;

/** Density steps in slider order, so the range input maps index → density. */
const DENSITY_ORDER: GridDensity[] = ['compact', 'comfort', 'spacious'];

/** In the Manage Columns panel, the first N columns (in table order) are locked
 *  on — the identity + core columns a reviewer always needs. The rest toggle. */
const LOCKED_COLUMN_COUNT = 5;

/** Sizes shown as a span plus a count; a list of 8–12 values swamps a cell. */
const SIZE_ORDER = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  'XXXL',
  '0X',
  '1X',
  '2X',
  '3X',
  '4X',
  '2Y',
  '4Y',
  '6Y',
  '8Y',
  '10Y',
  '12Y',
  '14Y',
];

function sizeSpan(sizes: string[] | undefined): string {
  if (!sizes || sizes.length === 0) return '—';
  const known = sizes.filter((size) => SIZE_ORDER.includes(size.toUpperCase()));
  if (known.length < 2) return `${String(sizes.length)} ${sizes.length === 1 ? 'size' : 'sizes'}`;
  const sorted = [...known].sort(
    (a, b) => SIZE_ORDER.indexOf(a.toUpperCase()) - SIZE_ORDER.indexOf(b.toUpperCase()),
  );
  return `${sorted[0] ?? ''}–${sorted[sorted.length - 1] ?? ''}`;
}

/** `gender` + `ageGroup` as one phrase; separately they're two near-empty cells. */
function audienceOf(row: Product): string {
  const gender = row.gender?.toLowerCase();
  const genderLabel =
    gender === 'female'
      ? "Women's"
      : gender === 'male'
        ? "Men's"
        : gender === 'unisex'
          ? 'Unisex'
          : null;
  const age = row.ageGroup?.toLowerCase();
  const ageLabel = age && age !== 'adult' ? age.charAt(0).toUpperCase() + age.slice(1) : null;
  const joined = [genderLabel, ageLabel].filter(Boolean).join(' · ');
  return joined === '' ? '—' : joined;
}

/** Dense row set: a 40px thumbnail with the title and retailer id stacked, then
 *  the attributes that distinguish one row from the next. Roughly half the row
 *  height of the previous 64px-thumbnail layout, so twice as many products are
 *  reviewable per screen. */
/** Built per-render rather than declared as a module constant, because the
 *  quick-view column needs the page's dialog setter. */
/**
 * Table columns for the products list. `showScore` is resolved from the page's own
 * data rather than assumed: with no enrichment scores on the wire the column would
 * be nothing but em-dashes, and an empty column still reads as "this product has
 * no score" instead of "this score isn't available yet".
 */
function buildProductColumns(
  onQuickView: (product: Product) => void,
  showScore: boolean,
): DataTableColumn<Product>[] {
  // Annotated rather than inferred: filtering the literal below would otherwise
  // strip the contextual type and leave every `cell(row)` param implicitly `any`.
  const columns: DataTableColumn<Product>[] = [
    {
      id: 'title',
      header: 'Product',
      accessorFn: (row) => row.title,
      cell: (row) => (
        <div className="flex items-center gap-2.5">
          <CachedImage src={row.imageUrl} alt="" className="size-10 shrink-0 rounded-md" />
          <div className="min-w-0">
            <div className="flex min-w-0 items-start gap-1.5">
              <p
                data-testid={`products-table-row-${row.id}-title`}
                className="line-clamp-2 max-w-100 text-sm leading-snug font-semibold"
              >
                {row.title}
              </p>
              {/* Strict `=== false`: the flag is merged in from a separate batch
                  call, so `undefined` means "not reported yet" — badging on it
                  would flash the warning on every page load. */}
              {row.isEnriched === false && <EnrichmentFailedBadge className="mt-0.5 shrink-0" />}
              {/* Sample-pass origin marker; renders only on `SAMPLE` rows. */}
              <EnrichmentPhaseBadge phase={row.enrichmentPhase} className="mt-0.5 shrink-0" />
              {/* Renders only when the run saw an older version than the live
                  row holds; absent version data renders nothing at all. */}
              <RecuratedSinceChip product={row} className="mt-0.5" />
            </div>
            <SplitLabelCell
              value={row.sourceProductId}
              delimiter={ID_DELIMITERS}
              className="text-muted-foreground mt-0.5 block font-mono text-xs"
              testId={`products-table-row-${row.id}-source-id`}
            />
          </div>
        </div>
      ),
      className: 'min-w-75',
    },
    {
      id: 'enrichmentScore',
      header: 'Score',
      // Not server-sortable: `sort_by` is passed through verbatim and the curation
      // API doesn't accept either score field.
      enableSorting: false,
      // Shares scoreBadge with the card view so the two never disagree about which
      // score a product is showing.
      cell: (row) => {
        const badge = scoreBadge(row);
        if (!badge) return <span className="text-muted-foreground text-sm">—</span>;
        const delta = badge.delta == null ? null : Math.round(badge.delta);
        const rising = delta != null && delta > 0;
        const falling = delta != null && delta < 0;
        const Arrow = rising ? TrendingUp : falling ? TrendingDown : Minus;
        return (
          <div
            data-testid={`products-table-row-${row.id}-score`}
            className="flex flex-col gap-0.5"
            title={badge.label}
          >
            <div className="flex items-baseline gap-1">
              {/* Before → after, in the same warning/primary pairing the
                  enrichment meter uses, so a row and an opened product read the
                  same way. Collapses to the bare score with no baseline. */}
              {badge.before != null && (
                <>
                  <span className="text-warning text-sm font-bold tabular-nums">
                    {Math.round(badge.before)}
                  </span>
                  <ArrowRight className="text-muted-foreground size-2.5" aria-hidden="true" />
                </>
              )}
              <span className={cn('text-sm font-bold tabular-nums', scoreTone(badge.value))}>
                {Math.round(badge.value)}
                <span className="text-muted-foreground text-[10px] font-semibold">/100</span>
              </span>
            </div>
            {delta != null && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 text-[10px] font-bold tabular-nums',
                  rising ? 'text-success' : falling ? 'text-error' : 'text-muted-foreground',
                )}
              >
                <Arrow className="size-2.5" aria-hidden="true" />
                {rising && '+'}
                {delta === 0 ? '' : delta}
              </span>
            )}
          </div>
        );
      },
      className: 'w-32',
    },
    {
      id: 'brand',
      header: 'Brand',
      accessorFn: (row) => row.brand,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.brand}</p>
          <p className="text-muted-foreground truncate text-xs">{audienceOf(row)}</p>
        </div>
      ),
      className: 'min-w-36',
    },
    {
      id: 'category',
      header: 'Category',
      accessorFn: (row) => row.category,
      cell: (row) => (
        <ProductCategory value={row.category}>
          {(display) => (
            <SplitLabelCell
              value={display}
              delimiter=">"
              className="text-sm"
              testId={`products-table-row-${row.id}-category`}
            />
          )}
        </ProductCategory>
      ),
      className: 'min-w-36',
    },
    {
      id: 'variant',
      header: 'Colour / Size',
      enableSorting: false,
      // Each line renders only when it has a value; a row with neither shows a
      // single dash rather than one per line.
      cell: (row) => {
        const hasSizes = (row.sizes?.length ?? 0) > 0;
        if (!row.color && !hasSizes) {
          return (
            <p className="text-sm" data-testid={`products-table-row-${row.id}-variant-empty`}>
              —
            </p>
          );
        }
        return (
          <div className="min-w-0" data-testid={`products-table-row-${row.id}-variant`}>
            {row.color ? (
              <p
                className="truncate text-sm"
                data-testid={`products-table-row-${row.id}-color-value`}
              >
                {row.color}
              </p>
            ) : null}
            {hasSizes ? (
              <p
                className="text-muted-foreground truncate text-xs tabular-nums"
                data-testid={`products-table-row-${row.id}-sizes-value`}
              >
                {sizeSpan(row.sizes)}
              </p>
            ) : null}
          </div>
        );
      },
      className: 'min-w-32',
    },
    {
      id: 'trends',
      header: 'Trends & tags',
      enableSorting: false,
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <TagBadges
            testId={`products-table-row-${row.id}-trends`}
            items={row.trends}
            className="border-purple-200 bg-purple-50 text-purple-700"
          />
          <TagBadges
            testId={`products-table-row-${row.id}-tags`}
            items={row.tags}
            className="border-tag-chip-border bg-tag-chip-bg text-tag-chip-text"
          />
        </div>
      ),
      className: 'min-w-44',
    },
    {
      id: 'quickView',
      header: '',
      enableSorting: false,
      cell: (row) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid={`products-table-row-${row.id}-quick-view-button`}
          aria-label={`Quick view ${row.title}`}
          title="Quick view"
          onClick={(e) => {
            // The row itself navigates to the detail page — quick view must not.
            e.stopPropagation();
            onQuickView(row);
          }}
          className="size-7 cursor-pointer"
        >
          <Eye className="size-4" />
        </Button>
      ),
      className: 'w-12',
    },
    {
      id: 'sku',
      header: 'External Id',
      accessorFn: (row) => row.sku,
      cell: (row) => (
        <SplitLabelCell
          value={row.sku}
          delimiter={ID_DELIMITERS}
          className="font-mono text-xs"
          testId={`products-table-row-${row.id}-external-id`}
        />
      ),
      className: 'min-w-40',
    },
  ];
  return showScore ? columns : columns.filter((column) => column.id !== 'enrichmentScore');
}

/**
 * Whether a data-driven column has anything to show for the rows on screen.
 * Only consulted when a host opts into `dropEmptyColumns` — the curation list
 * fills all of these, but the content-enrichment previews carry no brand,
 * audience, sizes, tags or trends, and a column of em-dashes reads as "this
 * product has none" rather than "this isn't available here".
 */
const COLUMN_HAS_DATA: Record<string, (products: Product[]) => boolean> = {
  brand: (products) => products.some((p) => p.brand !== '' || !!p.gender || !!p.ageGroup),
  category: (products) => products.some((p) => p.category !== ''),
  variant: (products) => products.some((p) => !!p.color || (p.sizes?.length ?? 0) > 0),
  trends: (products) => products.some((p) => p.trends.length > 0 || p.tags.length > 0),
};

/** Re-exported from the link helper so existing hosts keep importing it from here. */
export type { ProductLinkContext };

interface ProductsListViewProps {
  /** Rendered on the toolbar's left side, in place of `title`/`subtitle`. Lets a
   *  page put its own control there — the products page passes its scope switcher,
   *  so the switcher and the search/filter/view controls share one row instead of
   *  stacking with a dead gutter between them. */
  leading?: ReactNode;
  /** Optional heading rendered on the toolbar's left side (e.g. "Products Needing Attention"). */
  title?: string;
  /** Optional subtitle below the heading; falls back to "Showing 1-N of M products". */
  subtitle?: string;
  /** Hide the toolbar subtitle entirely (e.g. when the page renders its own subtitle under the title). */
  hideSubtitle?: boolean;
  products: Product[];
  total: number;
  isLoading: boolean;
  /** True while a refetch is in flight with the previous page still on screen.
   *  The query uses `placeholderData: keepPreviousData`, so paging, sorting and
   *  filtering never set `isLoading` — without this the UI looks inert until the
   *  new page lands. */
  isFetching?: boolean;
  /** Page-level action rendered at the end of the toolbar row (e.g. Export), so
   *  it doesn't need a header row of its own above the list. */
  trailing?: ReactNode;
  /** Current view mode — controlled by parent. `grid` is the content-enrichment
   *  editable grid; catalog-operations callers only ever pass `card`/`table`, so
   *  the wider input is backward-compatible (a narrower value is still assignable). */
  viewMode: 'card' | 'table' | 'grid';
  /** Called when the user switches to a card/table view. Parent should reset page
   *  to 1. The editable-grid tab uses `onEditableGridSelect` instead, so this
   *  signature stays `card`/`table` and existing callers are untouched. */
  onViewModeChange: (mode: 'card' | 'table') => void;
  /** Adds a third "editable grid" tab to the view toggle (content-enrichment
   *  only). Off by default so catalog-operations shows the usual two views. */
  enableEditableGrid?: boolean;
  /** Fired when the operator selects the editable-grid tab (parent flips its own
   *  `viewMode` to `grid`). Only meaningful with `enableEditableGrid`. */
  onEditableGridSelect?: () => void;
  /** Renders the editable grid body for the current page of `products`. Called
   *  only while `viewMode === 'grid'`; the host injects the enrichment grid here
   *  so this component stays free of enrichment concerns. */
  renderGridView?: (products: Product[]) => ReactNode;
  /** Current 1-based page number — controlled by parent. */
  page: number;
  /** Current page size — controlled by parent. */
  pageSize: number;
  /** Called when the user navigates to a different page. */
  onPageChange: (page: number) => void;
  /** Called when the user selects a different page size. */
  onPageSizeChange: (size: number) => void;
  /** Tags outbound product-detail links with `?from=…` so the detail page can render the right breadcrumb. */
  productLinkContext?: ProductLinkContext;
  /** Forwarded to the detail link as `?tab=…` so the detail breadcrumb can
   *  return to the same tab the user came from. */
  activeTab?: ProductTab | undefined;
  /** Filter definitions for the drawer. Empty array hides the filter button. */
  filterDefinitions?: CurationFilterDefinition[];
  /** Currently applied filters (controlled by the parent). */
  appliedFilters?: ProductFilterValues;
  /** Callback fired when the user commits a new filter selection. */
  onFiltersChange?: (filters: ProductFilterValues) => void;
  /** Committed search string (controlled by the parent). Empty string = no search. */
  search?: string;
  /** Fired when the user commits a search (Enter) or clears the input. */
  onSearchSubmit?: (value: string) => void;
  /** Enables server-side table sorting. When provided, sortable column headers
   *  become interactive and this fires with the active column (`null` = cleared)
   *  and direction. Omit to render the table without sort controls. */
  onSortChange?: (sortBy: string | null, sortDir: 'asc' | 'desc') => void;
  /** Current sort column (controlled by the parent) — `null` = unsorted.
   *  Feeds DataTable's controlled `sorting` prop so an externally-restored
   *  sort (e.g. after navigating back to this page) renders correctly. */
  sortBy?: string | null;
  /** Current sort direction. Only meaningful when `sortBy` is non-null. */
  sortDir?: 'asc' | 'desc';
  /** When true, disables search and filter controls (e.g. while the batch/jobs API is still loading). */
  isControlsDisabled?: boolean;
  /** Opens a product in place instead of navigating to its detail route. Set by
   *  hosts outside catalog-operations (the content-enrichment funnel opens a
   *  modal so the operator stays in the wizard). Applies to both view modes. */
  onOpenProduct?: (product: Product) => void;
  /** Hides columns no row on screen populates. Off by default: the curation list
   *  fills every column, so an empty one there is a real signal about the product.
   *  Hosts whose payload simply doesn't carry a column opt in. */
  dropEmptyColumns?: boolean;
  /** Drops the left filter rail, leaving the results pane full width. Filtering
   *  stays available through the toolbar's Filter drawer. */
  hideFilterRail?: boolean;
  /** Caps the table body at this pixel height with an inner scroll and a pinned
   *  header, instead of growing to fill the parent. Omit to keep `fillHeight`. */
  maxHeight?: number;
  /** Opts the table view into a "Columns" side panel that toggles the optional
   *  columns. Off by default so existing hosts are unchanged. */
  enableManageColumns?: boolean;
  /** Extra columns appended after the built-in set — host-specific fields (e.g.
   *  the enrichment attributes) that aren't part of the shared column builder.
   *  They land past the locked core, so they're always toggleable. */
  extraColumns?: DataTableColumn<Product>[];
  /** Column-id → replacement header, for a built-in column whose meaning differs
   *  per host (e.g. `sku` carries the Lily product id in the enrichment flow). */
  columnHeaderOverrides?: Record<string, string>;
  /** Column ids hidden on first render (still toggleable) — lets a host keep the
   *  default view lean while offering extra columns behind the Columns panel. */
  defaultHiddenColumnIds?: readonly string[];
  /** Persists the operator's column choice across remounts (e.g. switching funnel
   *  steps) and sessions, keyed by this stable table id in the preferences store.
   *  Omit to keep the selection session-scoped (reset on unmount). Only meaningful
   *  with `enableManageColumns`. */
  columnsStorageKey?: string;
  /** Drops the quick-view control from rows and tiles. Set by hosts whose
   *  products aren't in the curation store yet — the dialog reads product detail
   *  and enrichment, which for a sample product answer with nothing or with a
   *  previous run's values. */
  hideQuickView?: boolean;
}

/**
 * Reusable products list with search + filter + view toggle + table/card + pagination.
 * Used both inside the per-batch ProductsPage tabs and the standalone Needs Attention list.
 * Each product navigates to `/catalog-operations/<product.batchId>/<product.id>` on click.
 */
export function ProductsListView({
  leading,
  title,
  subtitle,
  hideSubtitle = false,
  products,
  total,
  isLoading,
  isFetching = false,
  trailing,
  viewMode,
  onViewModeChange,
  enableEditableGrid = false,
  onEditableGridSelect,
  renderGridView,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  productLinkContext,
  activeTab,
  filterDefinitions = [],
  appliedFilters = {},
  onFiltersChange,
  search: committedSearch = '',
  onSearchSubmit,
  onSortChange,
  sortBy = null,
  sortDir = 'asc',
  isControlsDisabled = false,
  onOpenProduct,
  dropEmptyColumns = false,
  hideFilterRail = false,
  hideQuickView = false,
  enableManageColumns = false,
  maxHeight,
  extraColumns,
  columnHeaderOverrides,
  defaultHiddenColumnIds,
  columnsStorageKey,
}: ProductsListViewProps) {
  const navigate = useNavigate();
  // Local draft of the input — only fires the API request when the user hits
  // Enter or clears the field. The parent owns the committed value.
  const [searchDraft, setSearchDraft] = useState(committedSearch);
  const [lastCommitted, setLastCommitted] = useState(committedSearch);
  // Re-seed the draft if the parent's committed value changes externally
  // (e.g. tab switch resets search) without using an effect.
  if (committedSearch !== lastCommitted) {
    setLastCommitted(committedSearch);
    setSearchDraft(committedSearch);
  }
  const [filterOpen, setFilterOpen] = useState(false);
  // Grid tile density. Scanning thousands of rows wants a different size than
  // checking twenty, so it's a control rather than a fixed choice.
  const [density, setDensity] = useState<GridDensity>('comfort');
  // Product open in the quick-view dialog; null = closed.
  const [quickView, setQuickView] = useState<Product | null>(null);
  // Any product on the page carrying an enrichment score is enough to keep the
  // column: a page where some rows scored and others didn't is exactly when the
  // em-dash is informative, whereas a page where none did has nothing to show.
  const hasAnyScore = products.some((product) => scoreBadge(product) !== null);
  const productColumns = useMemo(() => {
    const built = buildProductColumns(setQuickView, hasAnyScore);
    const quickViewFiltered = hideQuickView
      ? built.filter((column) => column.id !== 'quickView')
      : built;
    // Relabel built columns whose meaning differs per host (e.g. `sku` = product
    // id in the enrichment flow), then append the host's extra columns.
    const relabeled = columnHeaderOverrides
      ? quickViewFiltered.map((c) =>
          columnHeaderOverrides[c.id] !== undefined
            ? { ...c, header: columnHeaderOverrides[c.id] }
            : c,
        )
      : quickViewFiltered;
    const columns = extraColumns ? [...relabeled, ...extraColumns] : relabeled;
    // Manage Columns shows the full set and lets the operator hide what they
    // don't need, so it overrides the auto-drop of empty columns.
    if (!dropEmptyColumns || enableManageColumns) return columns;
    // Same reasoning as the score column above, applied to the rest of the
    // data-driven set — see COLUMN_HAS_DATA.
    return columns.filter((column) => COLUMN_HAS_DATA[column.id]?.(products) ?? true);
  }, [
    setQuickView,
    hasAnyScore,
    dropEmptyColumns,
    enableManageColumns,
    hideQuickView,
    products,
    extraColumns,
    columnHeaderOverrides,
  ]);

  // Manage Columns — the real (non-action) columns in table order. The first
  // LOCKED_COLUMN_COUNT are locked on; the rest toggle.
  const [columnsPanelOpen, setColumnsPanelOpen] = useState(false);
  const manageableColumns = useMemo(
    () => productColumns.filter((c) => c.id !== 'quickView'),
    [productColumns],
  );
  const lockedColumnIds = useMemo(
    () => new Set(manageableColumns.slice(0, LOCKED_COLUMN_COUNT).map((c) => c.id)),
    [manageableColumns],
  );

  // Column choice can persist (across step remounts and sessions) or stay
  // session-scoped, depending on whether the host supplied a `columnsStorageKey`.
  // Either way the hidden set is seeded from `defaultHiddenColumnIds`, and a
  // locked column can never be in it.
  const persistColumns = enableManageColumns && columnsStorageKey !== undefined;
  const columnVisibilityDefaults = useMemo<ColumnVisibilityDefault[]>(() => {
    const hidden = new Set(defaultHiddenColumnIds ?? []);
    return manageableColumns.map((c) => ({ key: c.id, defaultVisible: !hidden.has(c.id) }));
  }, [manageableColumns, defaultHiddenColumnIds]);
  // Always called (Rules of Hooks); the '' key is inert when persistence is off —
  // it's never written to, and its read is ignored below.
  const [persistedVisibility, setPersistedColumnVisible] = useTableColumnVisibility(
    columnsStorageKey ?? '',
    columnVisibilityDefaults,
  );
  const [localHiddenColumnIds, setLocalHiddenColumnIds] = useState<Set<string>>(
    () => new Set(defaultHiddenColumnIds ?? []),
  );
  const hiddenColumnIds = useMemo(
    () =>
      persistColumns
        ? new Set(
            columnVisibilityDefaults
              .filter((c) => persistedVisibility[c.key] === false)
              .map((c) => c.key),
          )
        : localHiddenColumnIds,
    [persistColumns, columnVisibilityDefaults, persistedVisibility, localHiddenColumnIds],
  );
  const setColumnHidden = useCallback(
    (columnId: string, hidden: boolean) => {
      if (persistColumns) {
        setPersistedColumnVisible(columnId, !hidden);
        return;
      }
      setLocalHiddenColumnIds((prev) => {
        const next = new Set(prev);
        if (hidden) next.add(columnId);
        else next.delete(columnId);
        return next;
      });
    },
    [persistColumns, setPersistedColumnVisible],
  );
  const visibleColumns = useMemo(
    () =>
      enableManageColumns
        ? // Locked columns always render, whatever the (seeded) hidden set says.
          productColumns.filter((c) => lockedColumnIds.has(c.id) || !hiddenColumnIds.has(c.id))
        : productColumns,
    [enableManageColumns, productColumns, hiddenColumnIds, lockedColumnIds],
  );

  // Drag-reorder order over the visible columns, persisted (like visibility)
  // under `columnsStorageKey` so it survives step switches and reloads; the
  // pinned Product column stays leftmost regardless of the order.
  const orderableColumnIds = useMemo(() => visibleColumns.map((c) => c.id), [visibleColumns]);
  const [columnOrder, setColumnOrder] = useTableColumnOrder(columnsStorageKey, orderableColumnIds);

  // Every filter the catalog exposes goes in the rail, in API order — a
  // multi_select with no options is dropped, since an empty checkbox list is
  // noise (the rail's banner explains why a filter can be empty). No filters at
  // all → no rail, so there's no empty column.
  const railFacetKeys = filterDefinitions
    .filter((d) => d.type !== 'multi_select' || d.options.length > 0)
    .map((d) => d.key);
  // A host can suppress the rail without giving up filtering — the toolbar's
  // Filter drawer and the applied-filter chips render from the same definitions.
  const hasRail = railFacetKeys.length > 0 && !hideFilterRail;
  const appliedFilterCount = countAppliedFilters(appliedFilters);

  function commitSearch(value: string) {
    onSearchSubmit?.(value);
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSearch(searchDraft.trim());
    }
  }

  function handleSearchClear() {
    setSearchDraft('');
    commitSearch('');
  }

  const resolvedSubtitle = subtitle ?? (isLoading ? 'Loading…' : `${String(total)} products`);

  // Extracted so the resizable and rail-less branches render one definition
  // of the results column rather than two copies that can drift.
  const resultsPane = (
    <div
      data-testid="products-results"
      className="relative flex min-h-0 min-w-0 flex-col"
      aria-busy={isFetching || isLoading}
    >
      {/* Veil rather than a skeleton swap: the outgoing page stays readable
                and in place, so the grid doesn't jump on every page change. */}
      {isFetching && !isLoading && (
        <div className="bg-background/55 absolute inset-0 z-20 flex items-start justify-center pt-20">
          <span
            data-testid="products-fetching"
            role="status"
            className="flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-xs font-semibold shadow-sm"
          >
            <Loader2 className="text-primary size-3.5 animate-spin" aria-hidden="true" />
            Loading products
          </span>
        </div>
      )}
      {viewMode === 'table' ? (
        <DataTable<Product>
          testId="products-table"
          mode="server"
          // The card is height-bounded, so the table scrolls its own body
          // and keeps its pagination pinned below instead of running past
          // the card and taking the page scroll with it. A host that passes
          // `maxHeight` caps the body at that height instead of filling the
          // parent — same sticky-header inner scroll, just a fixed size.
          fillHeight={maxHeight === undefined}
          {...(maxHeight === undefined ? {} : { maxHeight: `${String(maxHeight)}px` })}
          // The card clips at its border, so the footer needs its own
          // inset — otherwise "Items per page" sits flush against the edge.
          paginationClassName="border-t px-4 py-3"
          columns={visibleColumns}
          data={products}
          rowKey={(r) => r.id}
          // Product stays frozen at the left while the rest scroll and reorder.
          pinnedLeftColumnId="title"
          enableColumnReordering
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          enableSorting={!!onSortChange}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={(p) => {
            onPageChange(p.pageIndex + 1);
            onPageSizeChange(p.pageSize);
          }}
          sorting={sortBy ? [{ id: sortBy, desc: sortDir === 'desc' }] : []}
          onSortingChange={(s) => {
            // Surface the active sort (first column only — the API sorts by one
            // column). `null` when cleared, so the parent falls back to default.
            const active = s[0];
            onSortChange?.(active?.id ?? null, active?.desc ? 'desc' : 'asc');
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          totalRows={total}
          emptyMessage="No products found."
          isLoading={isLoading}
          onRowClick={(row) => {
            // A host that opens products itself (the enrichment funnel's modal)
            // takes precedence — it may have no detail route to navigate to.
            if (onOpenProduct) {
              onOpenProduct(row);
              return;
            }
            // Cross-batch needs-attention rows route to the dedicated
            // /products-need-attention/{productId} URL — they aren't tied
            // to a single batch.
            if (usesNeedsAttentionDetailRoute(row, productLinkContext)) {
              void navigate({
                to: '/catalog-operations/products-need-attention/$productId',
                params: { productId: row.id },
              });
              return;
            }
            const search: { from?: ProductLinkContext; tab?: ProductTab } = {};
            if (activeTab) search.tab = activeTab;
            void navigate({
              to: '/catalog-operations/$batchId/products/$productId',
              params: { batchId: row.batchId, productId: row.id },
              search,
            });
          }}
        />
      ) : viewMode === 'grid' ? (
        <>
          {/* The editable grid owns its own virtualized scroll; the pagination
              row below stays pinned so page size and number are always reachable. */}
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {isLoading ? (
              <div data-testid="products-editable-grid-loading" className="space-y-2">
                {Array.from({ length: pageSize }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <p
                data-testid="products-editable-grid-empty"
                className="text-muted-foreground p-12 text-center text-sm"
              >
                No products found.
              </p>
            ) : (
              renderGridView?.(products)
            )}
          </div>
          <FullPagination
            testId="products-pagination"
            className="mt-4 border-t px-4 py-3"
            page={page}
            pageSize={pageSize}
            totalRows={total}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={onPageChange}
            onPageSizeChange={(size) => {
              onPageSizeChange(size);
              onPageChange(1);
            }}
          />
        </>
      ) : (
        <>
          {/* Only the tiles scroll; the pagination row below stays visible,
                    so page size and page number are always reachable. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div
                data-testid="products-cards-loading"
                className="grid grid-cols-1 gap-3.5 p-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5"
              >
                {Array.from({ length: pageSize }).map((_, i) => (
                  <Skeleton key={i} className="aspect-3/4 rounded-xl" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <p
                data-testid="products-cards-empty"
                className="text-muted-foreground p-12 text-center text-sm"
              >
                No products found.
              </p>
            ) : (
              <ProductsGridView
                products={products}
                density={density}
                showAttentionReason={activeTab === 'needs-attention'}
                {...(hideQuickView ? {} : { onQuickView: setQuickView })}
                {...(onOpenProduct && { onOpenProduct })}
                productLinkContext={productLinkContext}
                linkSearch={{
                  ...(activeTab ? { tab: activeTab } : {}),
                  ...(productLinkContext ? { from: productLinkContext } : {}),
                }}
              />
            )}
          </div>
          <FullPagination
            testId="products-pagination"
            className="mt-4 border-t px-4 py-3"
            page={page}
            pageSize={pageSize}
            totalRows={total}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={onPageChange}
            onPageSizeChange={(size) => {
              onPageSizeChange(size);
              onPageChange(1);
            }}
          />
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Fills whatever height the page gives it and never grows past it, so the
          toolbar and pagination stay put while only the results scroll. Requires
          the page root to be a flex column with a definite height. */}
      <div data-testid="products-list" className="flex min-h-0 flex-1 flex-col gap-4">
        {/* `flex-wrap` is a safety net, not a layout mode: the compaction below
            1180px keeps this on one row down to ~900px, and past that the
            controls drop to a second line instead of overlapping the leading
            slot (whose occupant may carry its own min-width). */}
        <div
          data-testid="products-toolbar"
          className="flex flex-wrap items-center justify-between gap-4"
        >
          <div className="min-w-0 flex-1">
            {leading ?? (
              <>
                {title && (
                  <h2 data-testid="products-list-title" className="text-lg font-semibold">
                    {title}
                  </h2>
                )}
                {!hideSubtitle && (
                  <p data-testid="products-list-subtitle" className="text-muted-foreground text-sm">
                    {resolvedSubtitle}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <div className="relative">
              <Search
                className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                aria-hidden="true"
              />
              <Input
                data-testid="products-search-input"
                placeholder="Search products — press Enter"
                value={searchDraft}
                disabled={isControlsDisabled}
                onChange={(e) => {
                  setSearchDraft(e.target.value);
                }}
                onKeyDown={handleSearchKeyDown}
                className={cn(
                  // Narrows below 1180px to buy room for the tabs, which hold a
                  // 420px floor at every width. 265px is the point where the
                  // placeholder still reads in full, so it's the floor here.
                  'h-10 w-72 rounded-md pr-9 pl-9 text-sm max-[1180px]:w-[265px]',
                  isControlsDisabled && 'cursor-not-allowed',
                )}
              />
              {searchDraft.length > 0 && !isControlsDisabled && (
                <button
                  data-testid="products-search-clear-button"
                  type="button"
                  aria-label="Clear search"
                  onClick={handleSearchClear}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer transition-colors"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <Button
              data-testid="products-filter-button"
              variant="outline"
              size="sm"
              disabled={isControlsDisabled}
              className={cn(
                'h-10 gap-1.5 rounded-md px-4 text-sm font-medium max-[1180px]:px-3',
                isControlsDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
              onClick={() => {
                setFilterOpen(true);
              }}
            >
              <Filter className="h-4 w-4" aria-hidden="true" />
              {/* Collapses to icon-only under 1180px. `sr-only` rather than
                  `hidden` keeps the button's accessible name intact, and being
                  position-absolute it drops out of the flex row so the gap
                  collapses with it. The applied-count badge stays visible — it's
                  the only signal that filters are active. */}
              <span className="max-[1180px]:sr-only">Filter</span>
              {appliedFilterCount > 0 && (
                <span
                  data-testid="products-filter-count-badge"
                  aria-label={`${String(appliedFilterCount)} filters applied`}
                  className="bg-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold text-white tabular-nums"
                >
                  {String(appliedFilterCount)}
                </span>
              )}
            </Button>
            {viewMode === 'card' && (
              // A slider, not three buttons: density is a continuum and the
              // endpoints label themselves, so no legend is needed.
              <label className="bg-muted hidden items-center gap-2 rounded-md px-3 py-2 sm:flex">
                <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                  Compact
                </span>
                <input
                  data-testid="products-density-input"
                  type="range"
                  min={0}
                  max={DENSITY_ORDER.length - 1}
                  step={1}
                  value={DENSITY_ORDER.indexOf(density)}
                  aria-label="Tile density"
                  onChange={(e) => {
                    setDensity(DENSITY_ORDER[Number(e.target.value)] ?? 'comfort');
                  }}
                  className="accent-primary w-20 cursor-pointer"
                />
                <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                  Spacious
                </span>
              </label>
            )}
            {/* Manage Columns — a side panel listing every column: the first few
                are locked on, the rest toggle. Table view only; the card view
                isn't column-based. */}
            {enableManageColumns && viewMode === 'table' && manageableColumns.length > 0 && (
              <Sheet open={columnsPanelOpen} onOpenChange={setColumnsPanelOpen}>
                <SheetTrigger asChild>
                  <Button
                    data-testid="products-columns-button"
                    variant="outline"
                    size="sm"
                    disabled={isControlsDisabled}
                    className={cn(
                      'h-10 shrink-0 gap-1.5 rounded-md px-4 text-sm font-medium max-[1180px]:px-3',
                      isControlsDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                  >
                    <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                    <span className="max-[1180px]:sr-only">Columns</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  data-testid="products-columns-sheet"
                  side="right"
                  className="flex w-full flex-col gap-0 p-0 sm:max-w-sm"
                >
                  <SheetHeader className="border-b px-6 py-4 text-left">
                    <SheetTitle className="text-lg leading-8 font-semibold">Columns</SheetTitle>
                    <SheetDescription className="text-sm">
                      Choose which columns to show. The first {LOCKED_COLUMN_COUNT} are always on.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="min-h-0 flex-1 overflow-y-auto p-3">
                    <ul className="space-y-0.5">
                      {manageableColumns.map((c) => {
                        const locked = lockedColumnIds.has(c.id);
                        const checked = locked || !hiddenColumnIds.has(c.id);
                        return (
                          <li key={c.id}>
                            <label
                              className={cn(
                                'flex items-center gap-3 rounded-md px-3 py-2.5',
                                locked ? 'cursor-not-allowed' : 'hover:bg-muted/60 cursor-pointer',
                              )}
                            >
                              <Checkbox
                                data-testid={`products-columns-${c.id}-checkbox`}
                                checked={checked}
                                disabled={locked}
                                aria-label={typeof c.header === 'string' ? c.header : c.id}
                                onCheckedChange={(value) => {
                                  if (locked) return;
                                  setColumnHidden(c.id, value !== true);
                                }}
                              />
                              <span
                                className={cn(
                                  'flex-1 text-sm',
                                  locked ? 'text-muted-foreground' : 'text-foreground',
                                )}
                              >
                                {typeof c.header === 'string' && c.header ? c.header : c.id}
                              </span>
                              {locked && (
                                <Lock
                                  className="text-muted-foreground size-3.5 shrink-0"
                                  aria-hidden="true"
                                />
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </SheetContent>
              </Sheet>
            )}
            <SegmentedTabs
              value={viewMode}
              // The editable-grid tab flips the parent's mode via its own callback,
              // so the shared card/table handler keeps its narrow signature.
              onChange={(next) => {
                if (next === 'grid') onEditableGridSelect?.();
                else onViewModeChange(next);
              }}
              options={[
                {
                  value: 'card',
                  label: <LayoutGrid className="h-4 w-4" aria-hidden="true" />,
                  ariaLabel: 'Card view',
                  tooltip: 'Card view',
                },
                {
                  value: 'table',
                  label: <LayoutList className="h-4 w-4" aria-hidden="true" />,
                  ariaLabel: 'Table view',
                  tooltip: 'Table view',
                },
                ...(enableEditableGrid
                  ? [
                      {
                        value: 'grid' as const,
                        label: <Table2 className="h-4 w-4" aria-hidden="true" />,
                        ariaLabel: 'Editable grid view',
                        tooltip: 'Editable grid',
                      },
                    ]
                  : []),
              ]}
              ariaLabel="Product view mode"
              testId="products-view-tabs"
              size="icon"
              className="bg-muted w-auto border-0 sm:max-w-none"
            />
            {trailing}
          </div>
        </div>

        {onFiltersChange && (
          <AppliedFilterChips
            testId="products"
            definitions={filterDefinitions}
            applied={appliedFilters}
            onChange={onFiltersChange}
            className="px-1 pb-1"
          />
        )}

        {/* Rail and results form a resizable split, reusing ResizableSplit from the
            product-detail Overview rather than growing a second implementation of
            the same drag behaviour. `min-h-0 flex-1` makes it fill the page's
            remaining height, so only the results scroll while the toolbar and
            pagination stay put. */}
        {hasRail && onFiltersChange ? (
          <ResizableSplit
            defaultLeftPct={16}
            minLeftPct={11}
            maxLeftPct={40}
            className="min-h-0 flex-1 items-stretch overflow-hidden rounded-xl border bg-white"
            left={
              <ProductFilterRail
                testId="products-filter-rail"
                definitions={filterDefinitions}
                applied={appliedFilters}
                onChange={onFiltersChange}
                collapsedByDefault={railFacetKeys.slice(2)}
                disabled={isControlsDisabled}
                className="hidden lg:flex"
              />
            }
            right={resultsPane}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-hidden rounded-xl border bg-white">
            {resultsPane}
          </div>
        )}
      </div>

      <ProductQuickViewDialog
        product={quickView}
        // A host that opens products itself has no catalog-operations route to
        // send the user to, so the dialog's "Open full detail" link is dropped
        // rather than pointing somewhere wrong.
        hideDetailLink={!!onOpenProduct}
        productLinkContext={productLinkContext}
        open={quickView !== null}
        onOpenChange={(next) => {
          if (!next) setQuickView(null);
        }}
      />

      <ProductFilterDrawer
        open={filterOpen}
        onClose={() => {
          setFilterOpen(false);
        }}
        definitions={filterDefinitions}
        appliedFilters={appliedFilters}
        onApply={(filters) => {
          onFiltersChange?.(filters);
        }}
      />
    </>
  );
}
